import React, { useState, useEffect } from 'react';
import { Volume2, Play, SkipForward, RotateCcw, X, Info } from 'lucide-react';

interface Hotspot {
    id: string;
    x: number;
    y: number;
    label: string;
    explanation_ar: string;
    part_number?: string;
}

interface DiagramViewerProps {
    isOpen: boolean;
    onClose: () => void;
    diagramLabel: string;
    explanation: string;
    hotspots: Hotspot[];
    onVoiceCommand?: (command: string) => void;
}

export const DiagramViewer: React.FC<DiagramViewerProps> = ({
    isOpen,
    onClose,
    diagramLabel,
    explanation: _explanation,
    hotspots = []
}) => {
    const [activeHotspot, setActiveHotspot] = useState<Hotspot | null>(null);
    const [isWalkingThrough, setIsWalkingThrough] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(-1);

    useEffect(() => {
        if (!isOpen) {
            setIsWalkingThrough(false);
            setCurrentIndex(-1);
            setActiveHotspot(null);
        }
    }, [isOpen]);

    const handleNext = () => {
        if (currentIndex < hotspots.length - 1) {
            const nextIdx = currentIndex + 1;
            const nextHotspot = hotspots[nextIdx];
            if (nextHotspot) {
                setCurrentIndex(nextIdx);
                setActiveHotspot(nextHotspot);
                speak(nextHotspot.explanation_ar);
            }
        } else {
            setIsWalkingThrough(false);
        }
    };

    const handleRepeat = () => {
        if (activeHotspot) {
            speak(activeHotspot.explanation_ar);
        }
    };

    const speak = (text: string) => {
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.lang = 'ar-SA';
        window.speechSynthesis.speak(utterance);
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-xl flex items-center justify-center p-4 md:p-10 animate-in zoom-in duration-300">
            <div className="bg-charcoal-dark border border-white/10 rounded-[2.5rem] w-full max-w-5xl h-full flex flex-col overflow-hidden relative shadow-2xl">
                {/* Header */}
                <div className="p-6 border-b border-white/5 flex items-center justify-between bg-white/5">
                    <div className="flex items-center gap-4">
                        <div className="size-12 bg-blue-500/20 rounded-2xl flex items-center justify-center text-blue-400 border border-blue-500/20">
                            <span className="material-symbols-outlined">account_tree</span>
                        </div>
                        <div>
                            <h3 className="text-xl font-bold text-white leading-tight">المخطط الفني: {diagramLabel}</h3>
                            <p className="text-xs text-gray-500 font-mono tracking-widest uppercase">TECHNICAL SVG ENGINE v2.0</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {isWalkingThrough ? (
                            <div className="flex items-center gap-2 bg-primary/10 border border-primary/20 px-4 py-2 rounded-xl animate-pulse">
                                <Volume2 size={16} className="text-primary" />
                                <span className="text-[10px] font-bold text-primary uppercase">Voice Walkthrough Active</span>
                            </div>
                        ) : (
                            <button
                                onClick={() => { setIsWalkingThrough(true); handleNext(); }}
                                className="flex items-center gap-2 bg-white/5 hover:bg-white/10 px-4 py-2 rounded-xl text-xs font-bold text-gray-400"
                            >
                                <Play size={14} /> ابدأ الجولة الصوتية
                            </button>
                        )}
                        <button onClick={onClose} className="size-12 rounded-2xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400 transition-all">
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 overflow-hidden">
                    {/* Main Diagram Panel */}
                    <div className="lg:col-span-8 p-6 flex items-center justify-center bg-black/40 relative overflow-hidden">
                        <div className="relative w-full h-full max-h-[60vh] bg-white/5 rounded-3xl border border-white/5 flex items-center justify-center">
                            {/* Interactive SVG Layer */}
                            <svg viewBox="0 0 800 500" className="w-full h-full p-8 opacity-40">
                                {/* Base Chassis Mockup */}
                                <path d="M100 250 Q100 100 400 100 T700 250 T400 400 T100 250" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-600" />
                                <rect x="350" y="150" width="100" height="80" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-500" />
                            </svg>

                            {/* Hotspots */}
                            {hotspots.map((h, i) => (
                                <button
                                    key={h.id}
                                    style={{ left: `${h.x}%`, top: `${h.y}%` }}
                                    onClick={() => { setActiveHotspot(h); setCurrentIndex(i); speak(h.explanation_ar); }}
                                    className={`absolute size-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 transition-all flex items-center justify-center group ${activeHotspot?.id === h.id ? 'bg-primary border-primary shadow-[0_0_20px_rgba(var(--primary),0.5)] scale-125' : 'bg-primary/20 border-primary/40 hover:bg-primary/40'
                                        }`}
                                >
                                    <span className="text-[10px] font-black text-white">{i + 1}</span>

                                    {/* Tooltip on Hover */}
                                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 px-3 py-1.5 bg-background-dark border border-white/10 rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                                        <span className="text-[10px] font-bold text-white">{h.label}</span>
                                    </div>
                                </button>
                            ))}
                        </div>

                        {/* Hands-free Voice Controls */}
                        {isWalkingThrough && (
                            <div className="absolute bottom-10 left-1/2 -translate-x-1/2 flex gap-3 p-4 bg-charcoal-dark/80 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl">
                                <button onClick={handleRepeat} className="size-10 rounded-xl bg-white/5 hover:bg-white/10 flex items-center justify-center text-gray-400" title="كرر الشرح">
                                    <RotateCcw size={18} />
                                </button>
                                <button onClick={handleNext} className="h-10 px-6 rounded-xl bg-primary text-background-dark font-black text-xs flex items-center gap-2 hover:scale-105 active:scale-95 transition-all">
                                    التالي <SkipForward size={16} />
                                </button>
                                <button onClick={() => setIsWalkingThrough(false)} className="size-10 rounded-xl bg-red-500/10 hover:bg-red-500/20 text-red-400 flex items-center justify-center" title="إيقاف">
                                    <Volume2 size={18} className="animate-pulse" />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Side Info Panel */}
                    <div className="lg:col-span-4 p-8 bg-charcoal-light/30 border-r border-white/5 overflow-y-auto">
                        {activeHotspot ? (
                            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                                <div className="flex items-center gap-3 mb-6">
                                    <div className="size-12 bg-primary/20 rounded-2xl flex items-center justify-center text-primary border border-primary/20 font-black text-lg">
                                        {currentIndex + 1}
                                    </div>
                                    <div>
                                        <span className="text-[10px] text-primary font-black tracking-widest uppercase mb-1 block">Component Detail</span>
                                        <h4 className="text-xl font-bold text-white">{activeHotspot.label}</h4>
                                    </div>
                                </div>

                                <div className="bg-white/5 rounded-[2rem] p-6 border border-white/5 mb-6">
                                    <p className="text-gray-300 text-sm leading-relaxed text-right dir-rtl mb-4 font-medium">
                                        {activeHotspot.explanation_ar}
                                    </p>
                                    {activeHotspot.part_number && (
                                        <div className="pt-4 border-t border-white/5 flex items-center justify-between">
                                            <span className="text-[10px] font-bold text-gray-500 uppercase">Part #</span>
                                            <span className="text-[10px] font-mono text-primary bg-primary/10 px-2 py-0.5 rounded border border-primary/20">{activeHotspot.part_number}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="bg-blue-500/5 border border-blue-500/10 rounded-2xl p-4 flex gap-3">
                                    <Info className="text-blue-400 shrink-0" size={18} />
                                    <p className="text-[10px] text-blue-300/80 leading-relaxed italic">
                                        "يا هندسة ركز مع العلامة دي، دي النقطة اللي فيها الحساس اللي مطلع ليك الكود."
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-center opacity-40">
                                <span className="material-symbols-outlined text-6xl mb-4">ads_click</span>
                                <p className="text-sm font-bold text-gray-500">اختر نقطة من المخطط للفحص</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer Meta */}
                <div className="p-4 bg-white/5 border-t border-white/5 flex items-center justify-between px-8">
                    <div className="flex items-center gap-6">
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                            <span className="size-1.5 bg-green-500 rounded-full"></span> HD SVG RENDERED
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-gray-500 font-bold uppercase tracking-wider">
                            <span className="size-1.5 bg-blue-500 rounded-full"></span> VOICE READY
                        </div>
                    </div>
                    <p className="text-[9px] text-gray-600 font-mono">DIAGNOSIS_LINKED_ID: RP-{Math.random().toString(36).substring(7).toUpperCase()}</p>
                </div>
            </div>
        </div>
    );
};
